import { Injectable, NgZone } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { fabric } from 'fabric';
import { clamp } from 'lodash-es';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { VPT } from '../state/temporary-user-metadata-repository.service';
import {
  makeCanvasFitToContent,
  ObjectAbsoluteCoords,
} from '../sessions/session/wb-canvas/fabric-utils';
import { WatchDogTimer } from '../sessions/session/wb-toolbar/toolbars/realtime-common';
import { SpaceRepository } from '../state/space.repository';
import { ObservableUtils } from '../utilities/ObservableUtils';
import { MoveKeyDownHoldTimeLimit, MoveVPTOnArrowKey, KeyboardArrows } from '../consts';
import { WheelZoomEventData } from '../sessions/session/wb-canvas/wb-canvas.component';
import { FabricObject } from '../sessions/session/items-canvas/board-items/board-items-manager';
import type { BoardItem } from '../sessions/session/items-canvas/board-items/board.item';
import { RealtimeSpaceService } from './realtime-space.service';
import { SessionSharedDataService } from './session-shared-data.service';
import { FlagsService } from './flags.service';
import { PanelLayoutService } from './panel-layout.service';

export enum FloatingItemPosition {
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
  HIDDEN = 'hidden',
}

export const MAX_ZOOM = 400;
export const MIN_ZOOM = 5;
export const MIN_ZOOM_FOR_TOUCHE_DEVICE = 25;

export const MAX_FIT_TO_VIEW_CONTENT_HEIGHT = 40000; // a height after which the canvas content won't be visible if we tried to fit its content

export interface BoundingRect {
  top: number;
  height: number;
  left: number;
  width: number;
}

export const DEFAULT_MAX_SCROLL_DELTA = 200;

export enum ObjectInViewportPosition {
  FULLY_CONTAINED = 'FULLY_CONTAINED',
  FULLY_OUTSIDE = 'FULLY_OUTSIDE',
  INTERSECT = 'INTERSECT',
  UNKNOWN = 'UNKNOWN',
}

const FIT_TO_VIEW_MIN_ZOOM = 1;
const FIT_TO_VIEW_HORIZONTAL_PADDING = 200;
const FIT_TO_VIEW_VERTICAL_PADDING = 200;

// ---------------------------------------------------------------------------
// Luna-focus viewport framing (pure, fabric-free math — unit-testable).
//
// Given the region Luna just moved focus to (`newRect`), the previous focus
// (`oldRect`), the current viewport + zoom, and the usable canvas size, compute
// the target viewport transform (zoom + board-space center) to animate to.
// See {@link SessionsVptService.animatePanTo} for how the result is applied.
// ---------------------------------------------------------------------------

/** Zoom fraction (fabric scale) floors/ceilings, mirroring MIN_ZOOM/MAX_ZOOM (percent). */
const MIN_ZOOM_FRACTION = MIN_ZOOM / 100; // 0.05
const MAX_ZOOM_FRACTION = MAX_ZOOM / 100; // 4.0

/** Below this zoom fraction the content is too small to read — don't zoom out past it. */
const LEGIBILITY_FLOOR = 0.6;
/** Screen-space breathing room (px) kept around the focus region. */
const PAD_SCREEN = 56;
/** Minimum fraction of a frame dimension kept as margin on a region's far edge. */
const EDGE_MARGIN_FRAC = 0.1;
/** Target fraction of the frame the NEW region should occupy in reading position. */
const SPLIT_FRAC = 0.65;
/** If the new region is already ≥ this fraction visible, don't move (anti-jitter). */
const MOSTLY_VISIBLE = 0.85;
/** Cap (board px, pre-zoom) on the runway shown below a section title on section-start. */
const SECTION_RUNWAY_MAX = 240;

/** A rectangle in board coordinates. `x`/`y` are the top-left corner. */
export interface FocusRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Inputs to {@link computeFocusTargetVpt} — all rects in BOARD coordinates. */
export interface FocusFramingInputs {
  /** The region Luna just moved focus to. */
  newRect: FocusRect;
  /** The previous focus region (old is always ABOVE or LEFT of new — see note). */
  oldRect: FocusRect;
  /** The current usable viewport rect (board coords). */
  currentViewport: FocusRect;
  /** Current fabric zoom (scale; 1 = 100%). */
  zoom: number;
  /** Usable canvas width in screen px (full width minus panels/tower). */
  canvasW: number;
  /** Usable canvas height in screen px (full height minus top toolbar). */
  canvasH: number;
  /** Why focus moved — folds into the framing (see algorithm). */
  reason: 'section-start' | 'writing';
}

/** Output of {@link computeFocusTargetVpt}: a board-space center + zoom to frame to. */
export interface FocusFramingTarget {
  /** Target fabric zoom (scale). */
  zoom: number;
  /** Board-space X the usable-viewport center should land on. */
  centerX: number;
  /** Board-space Y the usable-viewport center should land on. */
  centerY: number;
}

function unionRect(a: FocusRect, b: FocusRect): FocusRect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const right = Math.max(a.x + a.width, b.x + b.width);
  const bottom = Math.max(a.y + a.height, b.y + b.height);
  return { x, y, width: right - x, height: bottom - y };
}

function padRect(r: FocusRect, p: number): FocusRect {
  return { x: r.x - p, y: r.y - p, width: r.width + 2 * p, height: r.height + 2 * p };
}

function intersectionArea(a: FocusRect, b: FocusRect): number {
  const w = Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x));
  const h = Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));
  return w * h;
}

/** Fraction of `inner`'s area that falls inside `outer` (1 = fully visible). */
function fractionVisible(inner: FocusRect, outer: FocusRect): number {
  const area = inner.width * inner.height;
  if (area <= 0) {
    return 1; // degenerate region → treat as visible so we never move for it
  }
  return intersectionArea(inner, outer) / area;
}

/**
 * Branch 1 helper (KEEP zoom): pick a frame center on one axis that biases the
 * NEW region into the reading position (~SPLIT_FRAC of the frame, far edge kept
 * ≥ EDGE_MARGIN_FRAC from the frame edge) while still fully containing the padded
 * union so the OLD region bleeds in from the anchor (min) side.
 */
function biasedContainCenter1D(
  nMin: number,
  nMax: number,
  uMin: number,
  uMax: number,
  frameDim: number,
): number {
  const nCenter = (nMin + nMax) / 2;
  // Place the new region's center at the reading position within the frame.
  const readingFrac = 1 - SPLIT_FRAC / 2; // new center sits past the midpoint
  let fMin = nCenter - readingFrac * frameDim;
  let fMax = fMin + frameDim;
  // Never let the new region touch the far (max) edge.
  const margin = EDGE_MARGIN_FRAC * frameDim;
  if (nMax > fMax - margin) {
    fMax = nMax + margin;
    fMin = fMax - frameDim;
  }
  // Clamp so the padded union stays fully inside the frame (branch 1 guarantees it fits).
  if (fMin > uMin) {
    fMin = uMin;
    fMax = fMin + frameDim;
  }
  if (fMax < uMax) {
    fMax = uMax;
    fMin = fMax - frameDim;
  }
  return (fMin + fMax) / 2;
}

/**
 * Branch 3 helper (KEEP zoom, can't fit both): center the NEW region, then shift
 * the center TOWARD the OLD region by as much as possible without the new region
 * losing its ≥ EDGE_MARGIN_FRAC margin on the far side — so old bleeds in from the
 * anchor edge but new never goes edge-flush.
 */
function shiftTowardOldCenter1D(
  nMin: number,
  nMax: number,
  oMin: number,
  oMax: number,
  frameDim: number,
): number {
  const nCenter = (nMin + nMax) / 2;
  const oCenter = (oMin + oMax) / 2;
  const dir = Math.sign(oCenter - nCenter);
  const dist = Math.abs(oCenter - nCenter);
  const newHalfFrac = (nMax - nMin) / 2 / frameDim;
  const maxShift = Math.max(0, 0.5 - EDGE_MARGIN_FRAC - newHalfFrac) * frameDim;
  return nCenter + Math.min(dist, maxShift) * dir;
}

/**
 * LEFT-ALIGN helper: return the frame center that seats the new region's LEFT edge a
 * screen-pad in from the frame's left, so Luna's content reads left-to-right into the
 * clear area instead of being centered (which pushed the right side off-screen / behind
 * the right-docked video tower). If the region is wider than the frame the left edge
 * still wins — the START of what she's writing stays on screen.
 */
function leftAlignCenter1D(nMin: number, frameDim: number, pad: number): number {
  const frameMin = nMin - pad;
  return frameMin + frameDim / 2;
}

/**
 * Pure target-computation for the Luna-focus viewport. Returns the board-space
 * center + zoom to frame to, or `null` when no move is warranted (pre-check).
 * Guarantees `zoom ∈ [MIN_ZOOM_FRACTION, MAX_ZOOM_FRACTION]` and never > current
 * zoom (never zooms in). Kept fabric-free so it's unit-testable.
 */
export function computeFocusTargetVpt(input: FocusFramingInputs): FocusFramingTarget | null {
  const { newRect, oldRect, currentViewport, zoom, canvasW, canvasH, reason } = input;

  // 1. Pre-check — if the new region is already mostly on screen, don't move.
  if (fractionVisible(newRect, currentViewport) >= MOSTLY_VISIBLE) {
    return null;
  }

  const union = unionRect(oldRect, newRect);
  const unionPadded = padRect(union, PAD_SCREEN / zoom);

  const frameWCur = canvasW / zoom;
  const frameHCur = canvasH / zoom;

  // Decide the zoom branch.
  let targetZoom = zoom;
  let branch: 1 | 2 | 3;
  if (unionPadded.width <= frameWCur && unionPadded.height <= frameHCur) {
    // Branch 1 — union already fits at current zoom: keep zoom, bias the frame.
    branch = 1;
    targetZoom = zoom;
  } else {
    const zFit = Math.min(canvasW / unionPadded.width, canvasH / unionPadded.height);
    if (zFit >= Math.max(LEGIBILITY_FLOOR, MIN_ZOOM_FRACTION) && zFit < zoom) {
      // Branch 2 — fits legibly if we zoom out: center the padded union.
      branch = 2;
      targetZoom = zFit;
    } else {
      // Branch 3 — can't fit both legibly: keep zoom, center new, reveal old.
      branch = 3;
      targetZoom = zoom;
    }
  }

  // Global zoom guards: clamp + never zoom in.
  targetZoom = clamp(targetZoom, MIN_ZOOM_FRACTION, MAX_ZOOM_FRACTION);
  targetZoom = Math.min(targetZoom, zoom);

  const frameW = canvasW / targetZoom;
  const frameH = canvasH / targetZoom;
  const padBoard = PAD_SCREEN / targetZoom;

  let centerX: number;
  let centerY: number;

  if (branch === 2) {
    // Center the padded union.
    centerX = unionPadded.x + unionPadded.width / 2;
    centerY = unionPadded.y + unionPadded.height / 2;
  } else if (branch === 1) {
    // LEFT-align X (reveal her content rightward) instead of the reading-position bias.
    centerX = leftAlignCenter1D(newRect.x, frameW, padBoard);
    centerY = biasedContainCenter1D(
      newRect.y,
      newRect.y + newRect.height,
      unionPadded.y,
      unionPadded.y + unionPadded.height,
      frameH,
    );
  } else {
    // Branch 3 (keep zoom, can't fit both): LEFT-align X so the new content reads in
    // from the left; center-ish Y so the current line stays vertically comfortable.
    centerX = leftAlignCenter1D(newRect.x, frameW, padBoard);
    centerY = shiftTowardOldCenter1D(
      newRect.y,
      newRect.y + newRect.height,
      oldRect.y,
      oldRect.y + oldRect.height,
      frameH,
    );
  }

  // 5. reason fold-in.
  if (reason === 'section-start') {
    // TOP-ANCHOR: treat the effective new region as the title box plus runway
    // below, and place the title top a screen-pad below the frame top (not
    // centered) so the section reads from the top with room to grow downward.
    const runway = Math.max(newRect.height, Math.min(SECTION_RUNWAY_MAX, frameH - 2 * padBoard));
    const frameTop = newRect.y - padBoard;
    centerY = frameTop + frameH / 2;
    // Keep the whole title+runway region from overshooting the frame bottom.
    const regionBottom = newRect.y + runway;
    const frameBottom = frameTop + frameH;
    if (regionBottom > frameBottom - padBoard) {
      centerY = regionBottom + padBoard - frameH / 2;
    }
  } else {
    // 'writing' → LEFT-align X on the new region so Luna's line reads left-to-right into
    // the clear area (centering pushed the right side off-screen / behind the VCT tower);
    // center-ish Y on newBbox. The pre-check already suppressed the move for in-view writes.
    centerX = leftAlignCenter1D(newRect.x, frameW, padBoard);
    centerY = shiftTowardOldCenter1D(
      newRect.y,
      newRect.y + newRect.height,
      oldRect.y,
      oldRect.y + oldRect.height,
      frameH,
    );
  }

  return { zoom: targetZoom, centerX, centerY };
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class SessionsVptService {
  // The current viewportTransform for the frame
  public readonly viewportTransform = new BehaviorSubject<VPT | null>(null);

  private readonly _internalVPTTransformChanges$ = new Subject<boolean>();
  public readonly internalVPTTransformChanges$ = this._internalVPTTransformChanges$.asObservable();

  // the viewports for all frames, so we can restore vpt when switching frames
  public readonly viewportTransformMap: { [key: string]: VPT } = {};

  // Stores the last mouse position used for pan events
  private lastMousePosition = new BehaviorSubject(new fabric.Point(0, 0));
  public readonly lastMousePosition$ = this.lastMousePosition.asObservable();

  private vptWatchDogTimer = new WatchDogTimer(1000, false);
  readonly zoomComplete = new BehaviorSubject<boolean>(false);

  // sudo pan is a way to know the center of the viewport after the pan has been applied
  // without waiting for the actual pan to happen
  private sudoPan = { x: 0, y: 0 };

  // Observable that fires whenever the user zooms/pans
  private userInitiatedZoomOrPan = new Subject<boolean>();
  userInitiatedZoomOrPan$ = this.userInitiatedZoomOrPan.asObservable();

  // Cancel handle for an in-flight Luna-focus pan animation (fabric.util.animate),
  // so a newer animatePanTo or a user pan/zoom can abort it mid-flight.
  private focusPanCancel: (() => void) | null = null;

  constructor(
    private sharedDataService: SessionSharedDataService,
    private realtimeSpaceService: RealtimeSpaceService,
    private spaceRepo: SpaceRepository,
    private observerUtils: ObservableUtils,
    private zone: NgZone,
    private flagsService: FlagsService,
    private panelLayoutService: PanelLayoutService,
  ) {
    combineLatest([
      this.sharedDataService.socketReconnected,
      this.spaceRepo.activeSpaceSelectedBoardUid$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const fabricCanvas = this.sharedDataService.fabricCanvas;
        if (fabricCanvas?.viewportTransform) {
          this.updatePersistentLocationWatchdog(fabricCanvas.viewportTransform);
        }
      });

    this.zone.runOutsideAngular(() => {
      this.viewportTransform.pipe(this.observerUtils.debounceTime(250)).subscribe((x) => {
        const canvas = this.sharedDataService.fabricCanvas;
        if (canvas) {
          canvas.forEachObject((obj) => {
            obj.setCoords(true);
          });
        }
      });
    });

    // Abort an in-flight Luna-focus pan when the user takes over the viewport.
    // Our own animation frames call setVPT(..., explicitChange = false), which
    // emits `false` on internalVPTTransformChanges$, so they never self-abort.
    this._internalVPTTransformChanges$.pipe(untilDestroyed(this)).subscribe((explicitChange) => {
      if (explicitChange) {
        this.cancelFocusPan();
      }
    });
    this.userInitiatedZoomOrPan$.pipe(untilDestroyed(this)).subscribe(() => {
      this.cancelFocusPan();
    });
  }

  // Needed to compute a delta in some cases
  public setLastMousePosition(point: fabric.Point) {
    this.lastMousePosition.next(point);
  }

  // Computes the delta for a pan and updates all necessary data
  // The custom handler should mutate the vpt variable directly instead of returning a new value
  public handlePan<T extends Event>(
    event: T,
    customHandler?: (vpt: VPT, delta: [number, number], event: T) => void,
  ) {
    if (this.sharedDataService.vptLocked.getValue()) {
      return;
    }

    this.userInitiatedZoomOrPan.next(true);

    const fabricCanvas = this.sharedDataService.fabricCanvas;

    if (!fabricCanvas?.viewportTransform) {
      return;
    }
    // Luna workspace mode constrains the camera inside setViewportTransform, so pan a copy and
    // apply it there instead of editing the live array in place (which would bypass it).
    const constrained = !!fabricCanvas.viewportConstraint;
    const vpt = constrained ? [...fabricCanvas.viewportTransform] : fabricCanvas.viewportTransform;
    const { clientX, clientY } = this.getCoords(event);

    let deltaX = clientX - this.lastMousePosition.value.x;
    let deltaY = clientY - this.lastMousePosition.value.y;

    // If there is a wheel event we use that deltaX, otherwise compute it
    const wE = <WheelEvent>(<unknown>event);

    if (wE.deltaX !== undefined) {
      deltaX = this.calculateCappedDelta(-wE.deltaX);
    }
    if (wE.deltaY !== undefined) {
      deltaY = this.calculateCappedDelta(-wE.deltaY);
    }

    if (customHandler) {
      customHandler(vpt, [deltaX, deltaY], event);
    } else {
      vpt[4] += deltaX;
      vpt[5] += deltaY;
    }
    if (constrained) {
      fabricCanvas.setViewportTransform(vpt);
    }
    const appliedVpt = fabricCanvas.viewportTransform ?? vpt;
    this.setLastMousePosition(new fabric.Point(clientX, clientY));
    this.viewportTransform.next(appliedVpt);
    this._internalVPTTransformChanges$.next(true);
    this.updatePersistentLocationWatchdog(appliedVpt);
    fabricCanvas.requestRenderAll();
  }

  // Sets the vpt manually
  // overrideLock is used so remote changes can modify the vpt
  public setVPT(vpt: VPT, overrideLock?: boolean, explicitChange = true) {
    if (this.sharedDataService.vptLocked.getValue() && !overrideLock) {
      return;
    }

    const fabricCanvas = this.sharedDataService.fabricCanvas;

    fabricCanvas?.setViewportTransform(vpt);
    fabricCanvas?.requestRenderAll();
    // Emit what was applied: Luna workspace mode may have constrained it.
    const appliedVpt = fabricCanvas?.viewportTransform ?? vpt;
    this.viewportTransform.next(appliedVpt);
    this._internalVPTTransformChanges$.next(!overrideLock && explicitChange);
    this.updatePersistentLocationWatchdog(appliedVpt);
  }

  // Transforms the different types of events data into a uniform set of (x,y)
  public getCoords(event: Event): { clientX: number; clientY: number } {
    switch (event.type) {
      case 'pointermove':
      case 'pointerdown':
      case 'mousemove':
      case 'mousedown':
        const mE = <PointerEvent | MouseEvent>event;
        return { clientX: mE.clientX, clientY: mE.clientY };

      case 'touchmove':
      case 'touchstart':
        const tE = <TouchEvent>event;
        return { clientX: tE.touches?.[0]?.clientX, clientY: tE.touches?.[0]?.clientY };
    }

    return { clientX: 0, clientY: 0 };
  }

  // Zooms using the mouseWheelEvent
  public handleZoom(event: WheelZoomEventData, multiplier = 1): void {
    if (this.sharedDataService.vptLocked.getValue()) {
      return;
    }

    this.userInitiatedZoomOrPan.next(true);

    const fabricCanvas = this.sharedDataService.fabricCanvas;

    if (!fabricCanvas?.viewportTransform) {
      return;
    }

    const delta = event.deltaY * multiplier;
    const { offsetX, offsetY } = event;
    let zoom = fabricCanvas.getZoom();
    zoom *= 0.9975 ** delta;

    zoom = clamp(zoom, MIN_ZOOM / 100, MAX_ZOOM / 100);
    const point = new fabric.Point(offsetX, offsetY);
    this.zoomToPoint(point, zoom);
  }

  // Zooms to a point for a given zoom level
  // Updates the persistent location and updates the viewportTransform
  public zoomToPoint(
    point: fabric.Point,
    zoom: number,
    fireZoomComplete = false,
    whiteBoardResized = false,
  ): void {
    if (this.sharedDataService.vptLocked.getValue() && !whiteBoardResized) {
      return;
    }

    const fabricCanvas = this.sharedDataService.fabricCanvas;

    if (!fabricCanvas?.viewportTransform) {
      return;
    }

    // Luna workspace mode: clamp before zooming so the point under the cursor stays put.
    if (fabricCanvas.viewportZoomClamp) {
      zoom = fabricCanvas.viewportZoomClamp(zoom);
    }
    fabricCanvas.zoomToPoint(point, zoom);

    if (fabricCanvas.viewportTransform) {
      this.viewportTransform.next(fabricCanvas.viewportTransform);
      this._internalVPTTransformChanges$.next(!whiteBoardResized);
      this.updatePersistentLocationWatchdog(fabricCanvas.viewportTransform);
    }
    fabricCanvas?.requestRenderAll();
    if (fireZoomComplete) {
      this.zoomComplete.next(true);
    }
  }

  // Reset zoom to 100 % and back to startup point
  public resetCanvasToOrigin(): void {
    if (this.sharedDataService.vptLocked.getValue()) {
      return;
    }

    const fabricCanvas = this.sharedDataService.fabricCanvas;

    if (!fabricCanvas?.viewportTransform) {
      return;
    }

    // reset zoom to 100%
    const centerPoint = new fabric.Point(
      <number>fabricCanvas?.getCenter().left,
      <number>fabricCanvas?.getCenter().top,
    );
    fabricCanvas.zoomToPoint(centerPoint, 1);

    // reset view port
    fabricCanvas?.setViewportTransform([1, 0, 0, 1, 0, 0]);

    this.updateLocalVpt();
    fabricCanvas?.requestRenderAll();
  }

  makeCanvasFitItsContent(
    fabricCanvas?: fabric.Canvas,
    boardItems?: fabric.Object[],
    horizontalPadding = FIT_TO_VIEW_HORIZONTAL_PADDING,
    verticalPadding = FIT_TO_VIEW_VERTICAL_PADDING,
    considerVideoTower = false,
    customHeight?: number,
  ): void {
    if (this.sharedDataService.vptLocked.getValue()) {
      return;
    }

    fabricCanvas = fabricCanvas ?? this.sharedDataService.fabricCanvas;
    if (!fabricCanvas?.viewportTransform) {
      return;
    }

    boardItems = boardItems ?? fabricCanvas.getObjects();
    if (boardItems?.length === 0) {
      this.resetCanvasToOrigin();
      return;
    }

    const videoTower = document.getElementById('overlay-parent-container');
    const videoTowerWidth = considerVideoTower && videoTower ? videoTower.offsetWidth : 0;

    const canvasWidth =
      this.panelLayoutService.getVisibleCanvasWidth(fabricCanvas.getWidth()) - videoTowerWidth;

    const topToolbarHeight = this.sharedDataService.topControlsHeight;
    const canvasHeight = fabricCanvas.getHeight() - topToolbarHeight;

    makeCanvasFitToContent(
      fabricCanvas,
      boardItems,
      canvasWidth,
      canvasHeight,
      FIT_TO_VIEW_MIN_ZOOM,
      horizontalPadding,
      verticalPadding,
      customHeight,
    );

    this.updateLocalVpt();
  }

  private updateLocalVpt() {
    if (this.sharedDataService.fabricCanvas?.viewportTransform) {
      this.viewportTransform.next(this.sharedDataService.fabricCanvas?.viewportTransform);
      this._internalVPTTransformChanges$.next(true);
      this.updatePersistentLocationWatchdog(this.sharedDataService.fabricCanvas?.viewportTransform);
    }
  }

  // Updates the persistentLocationWatchdogTimer with the new vpt.
  // This stores a vpt for a given frame for a userID if it has not
  // changed for 1 second
  public updatePersistentLocationWatchdog(vpt: VPT): void {
    const frameUID = this.sharedDataService.getCurrentFrame()?.uid;

    if (!frameUID) {
      return;
    }

    this.vptWatchDogTimer.setFunc(() => {
      this.updatePersistentLocation(vpt, frameUID);
    });
  }

  // Stores the location of this user in the userMetadata
  public updatePersistentLocation(vpt: VPT, frameUID: string): void {
    const fabricCanvas = this.sharedDataService.fabricCanvas;
    if (!fabricCanvas || !frameUID) {
      return;
    }
    this.realtimeSpaceService.service.modifyTemporaryUserMetadata({
      location: { vpt, canvasSize: fabricCanvas.getCanvasSize(), frameUID },
    });
  }

  private calculateBoardItemsCoords(canvasItemsObjects: FabricObject[]): ObjectAbsoluteCoords[] {
    const canvasItemsPositions = canvasItemsObjects.map((item) => {
      // Board items know their own bounds (some, like PDFs, differ from their fabric size before being added to the canvas)
      const boardItem = item as Partial<BoardItem>;
      if (typeof boardItem.getBoardItemBoundingRect === 'function') {
        return boardItem.getBoardItemBoundingRect();
      }
      // Plain fabric objects (e.g. scribbles)
      return {
        top: item.top ?? 0,
        left: item.left ?? 0,
        width: item.width ?? 0,
        height: item.height ?? 0,
      };
    });

    const absoluteCoords: ObjectAbsoluteCoords[] = [...canvasItemsPositions].map((position) => ({
      tl_x: position.left,
      tl_y: position.top,
      br_x: position.left + position.width,
      br_y: position.top + position.height,
    }));

    return absoluteCoords;
  }

  public areAnyObjectsVisibleInViewport(canvasItemsObjects: FabricObject[]): boolean {
    if (!this.sharedDataService.fabricCanvas) {
      return false;
    }

    const canvasItemsCoords = this.calculateBoardItemsCoords(canvasItemsObjects);

    const objects = this.sharedDataService.fabricCanvas.getObjects() ?? [];

    const fabricObjectsCoords = objects.map((obj) => ({
      br_x: obj.aCoords?.br?.x || 0,
      br_y: obj.aCoords?.br?.y || 0,
      tl_x: obj.aCoords?.tl?.x || 0,
      tl_y: obj.aCoords?.tl?.y || 0,
    }));

    const absoluteCoords = [...fabricObjectsCoords, ...canvasItemsCoords];
    for (const coords of absoluteCoords) {
      if (this.isObjectInViewport(coords)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Finds if object with given aCoords is fully or partially out of viewport
   * @param objCoords: object's absolute coords (tl, br)
   * @returns boolean
   * */
  public isObjectOutOfViewport(objCoords: ObjectAbsoluteCoords): boolean {
    return [ObjectInViewportPosition.FULLY_OUTSIDE, ObjectInViewportPosition.INTERSECT].includes(
      this.getObjectToViewPortPosition(objCoords),
    );
  }

  /**
   * Finds if object with given aCoords is fully or partially in the current viewport
   * @param objCoords: object's absolute coords (tl, br)
   * @returns boolean
   */
  public isObjectInViewport(objCoords: ObjectAbsoluteCoords): boolean {
    return [ObjectInViewportPosition.FULLY_CONTAINED, ObjectInViewportPosition.INTERSECT].includes(
      this.getObjectToViewPortPosition(objCoords),
    );
  }

  /**
   * Determines the position of an object within the viewport.
   *
   * @param objCoords: object's absolute coords (tl, br).
   * @returns The position of the object in relation to the viewport:
   * - ObjectViewportPosition.FULLY_CONTAINED: The object is fully contained within the viewport.
   * - ObjectViewportPosition.FULLY_OUTSIDE: The object is fully outside the viewport.
   * - ObjectViewportPosition.INTERSECTING: The object intersects with the viewport.
   * - Returns undefined if the viewport bounding rect or necessary data is missing.
   */
  public getObjectToViewPortPosition(objCoords: ObjectAbsoluteCoords): ObjectInViewportPosition {
    const viewPortBoundingRect = this.getViewPortBoundingRect();

    if (
      !viewPortBoundingRect ||
      !this.sharedDataService.fabricCanvas?.width ||
      !this.sharedDataService.fabricCanvas?.vptCoords
    ) {
      return ObjectInViewportPosition.UNKNOWN;
    }

    const { top, bottom, left, right } = viewPortBoundingRect;

    const objectBoundingRect = {
      bottom: objCoords.br_y,
      left: objCoords.tl_x,
      top: objCoords.tl_y,
      right: objCoords.br_x,
    };

    // Check if the object is fully contained in the viewport
    if (
      objectBoundingRect.top >= top &&
      objectBoundingRect.bottom <= bottom &&
      objectBoundingRect.left >= left &&
      objectBoundingRect.right <= right
    ) {
      return ObjectInViewportPosition.FULLY_CONTAINED;
    }

    // Check if the object is fully outside the viewport
    if (
      objectBoundingRect.top > bottom ||
      objectBoundingRect.bottom < top ||
      objectBoundingRect.left > right ||
      objectBoundingRect.right < left
    ) {
      return ObjectInViewportPosition.FULLY_OUTSIDE;
    }

    // If the object is neither fully contained nor fully outside, it must intersect with the viewport
    return ObjectInViewportPosition.INTERSECT;
  }

  public panToObject(
    objWidth: number,
    objHeight: number,
    tl_x: number | undefined,
    tl_y: number | undefined,
  ): void {
    const canvasWidth =
      (this.sharedDataService.fabricCanvas as any).wrapperEl?.offsetWidth ??
      (this.sharedDataService.fabricCanvas as any).getWidth();

    const zoom = (this.sharedDataService.fabricCanvas as any).getZoom();
    let panX = 0;
    let panY = 0;
    panX = (canvasWidth / zoom / 2 - (tl_x ?? 0) - objWidth / 2) * zoom;
    panY =
      ((this.sharedDataService.fabricCanvas as any).getHeight() / zoom / 2 -
        (tl_y ?? 0) -
        objHeight / 2) *
      zoom;
    this.setVPT([zoom, 0, 0, zoom, panX, panY]);
  }

  /**
   * Calculates the ideal position for a floating item relative to a canvas item on a canvas.
   *
   * @param canvasItemBoundingRect - The bounding rectangle of the canvas item.
   * @param positionPreferences - An array of position preferences for the floating item.
   * @param neededHeightForFloatingItem - The needed height for the floating item (default: 45).
   * @param neededWidthForFloatingItem - The needed width for the floating item (default: 45).
   * @param scaleNeededSize - Flag indicating whether the needed size should be scaled based on the zoom level (default: false).
   * @returns The position for the floating item or undefined if required data is unavailable.
   */
  public getCanvasItemFloatingItemPosition(
    canvasItemBoundingRect: BoundingRect,
    positionPreferences: FloatingItemPosition[],
    neededHeightForFloatingItem = 45,
    neededWidthForFloatingItem = 45,
    scaleNeededSize = false,
  ): FloatingItemPosition | undefined {
    const zoom = this.sharedDataService.fabricCanvas?.getZoom();
    const viewPortBoundingRect = this.getViewPortBoundingRect();

    // Check if required data is available
    if (!viewPortBoundingRect || !zoom) {
      return undefined;
    }

    // Scale the needed size based on the zoom level if required
    if (scaleNeededSize) {
      neededWidthForFloatingItem /= zoom;
      neededHeightForFloatingItem /= zoom;
    }

    // Getting the borders of the canvas item
    const { top, height, left, width } = canvasItemBoundingRect;
    const floatingItemTop = top - neededHeightForFloatingItem;
    const floatingItemBottom = top + height + neededHeightForFloatingItem;
    const floatingItemLeft = left - neededWidthForFloatingItem;
    const floatingItemRight = left + width + neededWidthForFloatingItem;

    // Calculate positions and determine visibility
    const {
      top: viewportTop,
      bottom: viewportBottom,
      left: viewportLeft,
      right: viewportRight,
    } = viewPortBoundingRect;
    const isTopObscured = floatingItemTop < viewportTop;
    const isBottomObscured = floatingItemBottom > viewportBottom;
    const isLeftObscured = floatingItemLeft < viewportLeft;
    const isRightObscured = floatingItemRight > viewportRight;

    // Determine the new position for the floating item
    for (const preference of positionPreferences) {
      if (preference === FloatingItemPosition.TOP && !isTopObscured) {
        return FloatingItemPosition.TOP;
      } else if (preference === FloatingItemPosition.RIGHT && !isRightObscured) {
        return FloatingItemPosition.RIGHT;
      } else if (preference === FloatingItemPosition.BOTTOM && !isBottomObscured) {
        return FloatingItemPosition.BOTTOM;
      } else if (preference === FloatingItemPosition.LEFT && !isLeftObscured) {
        return FloatingItemPosition.LEFT;
      }
    }

    return FloatingItemPosition.HIDDEN;
  }

  handleArrowKeysMoveVPT(key: string, holdTime: number): void {
    let movementX = 0;
    let movementY = 0;
    const moveGap =
      holdTime <= MoveKeyDownHoldTimeLimit
        ? MoveVPTOnArrowKey.smallStep
        : MoveVPTOnArrowKey.bigStep;

    switch (key) {
      case KeyboardArrows.LEFT:
        movementX = +moveGap;
        break;
      case KeyboardArrows.RIGHT:
        movementX = -moveGap;
        break;
      case KeyboardArrows.UP:
        movementY = moveGap;
        break;
      case KeyboardArrows.DOWN:
        movementY = -moveGap;
        break;
    }

    this.panBy(movementX, movementY);
  }

  public getViewPortBoundingRect() {
    const zoom = this.sharedDataService.fabricCanvas?.getZoom();
    const vptCoords = this.sharedDataService?.fabricCanvas?.vptCoords;
    const canvasWidth = this.panelLayoutService.getVisibleCanvasWidth(
      this.sharedDataService.fabricCanvas?.width ?? 0,
    );
    if (!zoom || !canvasWidth || !vptCoords) {
      return undefined;
    }
    return {
      bottom: vptCoords.bl.y,
      left: vptCoords.bl.x,
      top: vptCoords.tr.y,
      right: vptCoords.bl.x + canvasWidth / zoom,
    };
  }

  public getCenterCoords(): { x: number; y: number } {
    const boundingRect = this.getViewPortBoundingRect();
    let x: number;
    let y: number;
    if (boundingRect) {
      x = boundingRect.left + (boundingRect.right - boundingRect.left) / 2;
      y = boundingRect.top + (boundingRect.bottom - boundingRect.top) / 2;
    } else if (this.sharedDataService.fabricCanvas) {
      return this.sharedDataService.fabricCanvas.getVpCenter();
    } else {
      x = 100;
      y = 100;
    }
    return { x: x + this.sudoPan.x, y: y + this.sudoPan.y };
  }

  public panBy(movementX: number, movementY: number): void {
    this.sudoPan.x += movementX;
    this.sudoPan.y += movementY;
    const canvas = this.sharedDataService.fabricCanvas as fabric.Canvas;
    const currentTransform = (canvas.viewportTransform || []).slice();

    // Apply the movement to the current transform matrix
    currentTransform[4] += movementX;
    currentTransform[5] += movementY;

    const centerPoint = new fabric.Point(
      <number>this.sharedDataService.fabricCanvas?.getCenter().left,
      <number>this.sharedDataService.fabricCanvas?.getCenter().top,
    );
    this.sharedDataService.fabricCanvas?.zoomToPoint(centerPoint, 1);

    // Set the updated transform matrix to the canvas
    canvas.setViewportTransform(currentTransform);
    canvas.requestRenderAll();
    this.viewportTransform.next(canvas.viewportTransform || []);
    this._internalVPTTransformChanges$.next(true);
    this.updatePersistentLocationWatchdog(canvas.viewportTransform || []);
    canvas.on('after:render', () => (this.sudoPan = { x: 0, y: 0 }));
  }

  public calculateCappedDelta(delta: number) {
    const maxDelta =
      (this.flagsService.featureFlagsVariables?.canvas_scroll_limit?.max_delta as number) ??
      DEFAULT_MAX_SCROLL_DELTA;
    return clamp(delta, -maxDelta, maxDelta);
  }

  private _getCenterVptCoordsOfObject(
    objWidth: number,
    objHeight: number,
    tl_x: number | undefined,
    tl_y: number | undefined,
  ): { zoom: number; xCoord: number; yCoord: number } {
    const canvasWidth =
      (this.sharedDataService.fabricCanvas as any).wrapperEl?.offsetWidth ??
      (this.sharedDataService.fabricCanvas as any).getWidth();

    const zoom = (this.sharedDataService.fabricCanvas as any).getZoom();
    let xCoord = 0;
    let yCoord = 0;
    xCoord = (canvasWidth / zoom / 2 - (tl_x ?? 0) - objWidth / 2) * zoom;
    yCoord =
      ((this.sharedDataService.fabricCanvas as any).getHeight() / zoom / 2 -
        (tl_y ?? 0) -
        objHeight / 2) *
      zoom;

    return { zoom, xCoord, yCoord };
  }

  private async animateToObject(
    endValueX: number,
    endValueY: number,
    duration: number,
  ): Promise<void> {
    const canvas = this.sharedDataService.fabricCanvas;
    if (!canvas) {
      throw new Error('Expected canvas to be defined');
    }
    const origZoom = canvas.getZoom();
    const origX = canvas.viewportTransform?.[4] ?? 0;
    const origY = canvas.viewportTransform?.[5] ?? 0;

    const panAnim = {
      x: origX,
      y: origY,
    };
    const done = {
      x: false,
      y: false,
    };
    return new Promise<void>((resolve) => {
      fabric.util.animate({
        startValue: origX,
        endValue: endValueX,
        duration: duration,
        easing: fabric.util.ease['easeOutSine'],
        onChange: function (value) {
          panAnim.x = value;
        },

        onComplete: () => {
          canvas.renderAll();
          done.x = true;

          if (done.y && done.x) {
            resolve();
          }
        },
      });

      fabric.util.animate({
        startValue: origY,
        endValue: endValueY,
        duration: duration,
        easing: fabric.util.ease['easeOutSine'],
        onChange: (value) => {
          panAnim.y = value;
          const viewportTransform = [origZoom, 0, 0, origZoom, panAnim.x, panAnim.y];
          this.setVPT(viewportTransform);
        },
        onComplete: () => {
          if (canvas.viewportTransform) {
            this.setVPT(canvas.viewportTransform);
          }
          done.y = true;
          if (done.y && done.x) {
            resolve();
          }
        },
      });
    });
  }

  public getCanvasSize(fabricCanvas?: fabric.Canvas): [number, number] {
    return [fabricCanvas?.getWidth() || 0, fabricCanvas?.getHeight() || 0];
  }

  public scrollToObject(
    objWidth: number,
    objHeight: number,
    tl_x: number | undefined,
    tl_y: number | undefined,
    duration: number,
  ) {
    const { xCoord, yCoord } = this._getCenterVptCoordsOfObject(objWidth, objHeight, tl_x, tl_y);
    return this.animateToObject(xCoord, yCoord, duration);
  }

  public scrollToCenter(duration: number) {
    return this.animateToObject(0, 0, duration);
  }

  /**
   * How much the video tower (VCT) actually occludes the board, measured live from
   * its on-screen rect relative to the canvas. The tower isn't always the same shape:
   * it's a right-docked COLUMN in the RIGHT / floating / minimized layouts, but a
   * TOP-centered bar in the default gallery layout — and its container is full-width
   * (`width: 100%`) in some layouts, so its raw `offsetWidth` is not a usable inset.
   * We therefore classify by position and return a directional inset:
   *  - `right`: px to carve off the RIGHT (a right-docked column) — the case in the
   *    screenshot where content lands behind the tiles.
   *  - `top`: px to carve off the TOP (a top-centered bar) — never shrinks width.
   * Returns zeros when the tower is absent/hidden.
   */
  private getVctOcclusion(canvasRect: DOMRect): { right: number; top: number } {
    const tower = document.getElementById('overlay-parent-container');
    if (!tower) {
      return { right: 0, top: 0 };
    }
    // Measure the ACTUAL visible tiles, not the container: in the floating/right
    // layouts the container is `width: 100%` (full-width) and only its child tiles
    // sit on the right, so the container rect would report no right dock. Union the
    // tile rects to get the tiles' true on-screen footprint. Fall back to the
    // container when no tiles are found (e.g. a single-element layout).
    const tiles = Array.from(tower.querySelectorAll('app-session-call-user')) as HTMLElement[];
    const rects = (tiles.length ? tiles : [tower])
      .map((el) => el.getBoundingClientRect())
      .filter((r) => r.width > 0 && r.height > 0);
    if (!rects.length) {
      return { right: 0, top: 0 };
    }
    const vctLeft = Math.min(...rects.map((r) => r.left));
    const vctRight = Math.max(...rects.map((r) => r.right));
    const vctTop = Math.min(...rects.map((r) => r.top));
    const vctBottom = Math.max(...rects.map((r) => r.bottom));

    const DOCK_TOL = 48; // px slack for "reaches the edge"
    const CLEARANCE = 24; // extra breathing room so framed content isn't flush to the tiles
    const cx = canvasRect.left + canvasRect.width / 2;
    const cy = canvasRect.top + canvasRect.height / 2;
    // Right-docked column: tiles start in the right half AND reach the right edge →
    // carve off the right, from where the tiles begin (minus clearance) to the edge.
    const rightDocked = vctLeft > cx && vctRight >= canvasRect.right - DOCK_TOL;
    const right = rightDocked ? Math.max(0, canvasRect.right - vctLeft + CLEARANCE) : 0;
    // Top bar: tiles pinned near the top and not reaching mid-height → carve off the
    // top only (a top/centered bar must NOT shrink the usable width).
    const topDocked = !rightDocked && vctTop <= canvasRect.top + DOCK_TOL && vctBottom < cy;
    const top = topDocked ? Math.max(0, vctBottom - canvasRect.top + CLEARANCE) : 0;
    return { right, top };
  }

  /**
   * The usable canvas size in screen px — the full canvas minus the side panels,
   * the video tower's ACTUAL occlusion (see {@link getVctOcclusion}) and the top
   * toolbar. `left`/`top` are the screen offsets of the usable region (so its center
   * is `left + width / 2`, `top + height / 2`).
   */
  private getUsableCanvasMetrics():
    | { width: number; height: number; left: number; top: number }
    | undefined {
    const fabricCanvas = this.sharedDataService.fabricCanvas;
    if (!fabricCanvas) {
      return undefined;
    }
    const { leftPanelWidth, rightPanelWidth } = this.sharedDataService.getPanelWidth();
    const topToolbarHeight = this.sharedDataService.topControlsHeight;

    // Measure the VCT's real footprint instead of blindly subtracting its width:
    // a right-docked column carves off the RIGHT (so framed content clears the tiles),
    // a top bar carves off the TOP. Screen px from getBoundingClientRect line up 1:1
    // with fabric's display px (getWidth/getHeight).
    const canvasRect = (
      fabricCanvas as unknown as { wrapperEl?: HTMLElement }
    ).wrapperEl?.getBoundingClientRect();
    const occ = canvasRect ? this.getVctOcclusion(canvasRect) : { right: 0, top: 0 };

    const left = leftPanelWidth;
    const top = Math.max(topToolbarHeight, occ.top);
    const width = Math.max(
      1,
      fabricCanvas.getWidth() - leftPanelWidth - rightPanelWidth - occ.right,
    );
    const height = Math.max(1, fabricCanvas.getHeight() - top);
    return { width, height, left, top };
  }

  /**
   * Frame the viewport onto `bbox` (BOARD coords) using the Luna-focus framing
   * algorithm ({@link computeFocusTargetVpt}) and animate there. Additive helper
   * for the Luna-focus feature — it does not alter any existing pan/zoom path.
   *
   * - `reason` folds into the framing (section-start top-anchors; writing centers).
   * - `oldBbox` is the previous focus; when omitted the current viewport rect is
   *   used (so a first focus just frames onto the new region).
   * - Never zooms IN, honors `vptLocked` (via {@link setVPT}), and is cancelable:
   *   a newer call or any user pan/zoom aborts the in-flight animation.
   */
  public animatePanTo(
    bbox: BoundingRect,
    opts?: {
      reason?: 'section-start' | 'writing';
      oldBbox?: BoundingRect | null;
      durationMs?: number;
    },
  ): void {
    if (this.sharedDataService.vptLocked.getValue()) {
      return;
    }
    const fabricCanvas = this.sharedDataService.fabricCanvas;
    const metrics = this.getUsableCanvasMetrics();
    const viewportRect = this.getViewPortBoundingRect();
    if (!fabricCanvas?.viewportTransform || !metrics || !viewportRect) {
      return;
    }

    const zoom = fabricCanvas.getZoom();
    const reason = opts?.reason ?? 'writing';
    const durationMs = opts?.durationMs ?? 350;

    // The USABLE viewport in board coords: the full visible rect inset by the side
    // panels + video tower, so the "already framed?" pre-check treats a section
    // hidden behind the VCT as NOT visible and still jumps. metrics.left/top are
    // screen px from the canvas origin; divide by zoom to convert insets to board units.
    const currentViewport: FocusRect = {
      x: viewportRect.left + metrics.left / zoom,
      y: viewportRect.top + metrics.top / zoom,
      width: metrics.width / zoom,
      height: metrics.height / zoom,
    };
    const newRect = this.boundingRectToFocusRect(bbox);
    const oldRect = opts?.oldBbox ? this.boundingRectToFocusRect(opts.oldBbox) : currentViewport;

    const target = computeFocusTargetVpt({
      newRect,
      oldRect,
      currentViewport,
      zoom,
      canvasW: metrics.width,
      canvasH: metrics.height,
      reason,
    });

    // Pre-check said "already framed" — nothing to do.
    if (!target) {
      return;
    }

    // Convert the board-space center + zoom into a viewport transform that lands
    // the target center at the center of the USABLE region (panel/tower aware).
    const screenCenterX = metrics.left + metrics.width / 2;
    const screenCenterY = metrics.top + metrics.height / 2;
    // Luna workspace mode cannot zoom below its minimum; aim with the zoom it will really use.
    const targetZoom =
      this.sharedDataService.fabricCanvas?.viewportZoomClamp?.(target.zoom) ?? target.zoom;
    const tx = screenCenterX - target.centerX * targetZoom;
    const ty = screenCenterY - target.centerY * targetZoom;

    this.animateToVpt([targetZoom, 0, 0, targetZoom, tx, ty], durationMs);
  }

  private boundingRectToFocusRect(rect: BoundingRect): FocusRect {
    return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
  }

  /** Cancel any in-flight Luna-focus pan animation. */
  private cancelFocusPan(): void {
    if (this.focusPanCancel) {
      this.focusPanCancel();
      this.focusPanCancel = null;
    }
  }

  /**
   * Animate the viewport transform to an arbitrary `[z, 0, 0, z, tx, ty]` target.
   * Generalizes {@link animateToObject} (which only pans at a fixed zoom) to also
   * interpolate zoom. Each frame calls `setVPT(..., explicitChange = false)` so it
   * respects `vptLocked` and does not read as a user-initiated change (and so does
   * not abort itself). A newer call or a user pan/zoom cancels it mid-flight.
   */
  public animateToVpt(target: VPT, duration: number): void {
    // A newer focus pan supersedes any in-flight one.
    this.cancelFocusPan();

    const canvas = this.sharedDataService.fabricCanvas;
    if (!canvas?.viewportTransform) {
      return;
    }

    const startZoom = canvas.getZoom();
    const startX = canvas.viewportTransform[4] ?? 0;
    const startY = canvas.viewportTransform[5] ?? 0;
    const [endZoom, , , , endX, endY] = target;

    // fabric.util.animate supports vector (array) start/end values at runtime
    // (its `isMany` path) but @types/fabric only types the scalar form — hence the
    // `any` cast on the options. The return value is the animation's cancel fn.
    this.focusPanCancel = fabric.util.animate({
      startValue: [startZoom, startX, startY],
      endValue: [endZoom, endX, endY],
      duration,
      easing: fabric.util.ease['easeOutSine'],
      onChange: ((value: number[]) => {
        const [z, x, y] = value;
        this.setVPT([z, 0, 0, z, x, y], false, false);
      }) as unknown as (value: number) => void,
      onComplete: () => {
        this.setVPT([endZoom, 0, 0, endZoom, endX, endY], false, false);
        this.focusPanCancel = null;
      },
    } as unknown as fabric.IUtilAnimationOptions) as unknown as () => void;
  }

  // Loads the stored viewport transform if there
  // is one, else it uses the default transformation
  public loadViewportTransform(frameUID: string) {
    // Luna workspace mode: a camera saved at another screen width means nothing on the column,
    // so open the board at its top; the constraint raises zoom 0 to the minimum.
    if (this.sharedDataService.fabricCanvas?.viewportConstraint) {
      this.setVPT([0, 0, 0, 0, 0, Number.MAX_SAFE_INTEGER], false, false);
      return;
    }
    const viewportTransform = this.viewportTransformMap[frameUID] || [1, 0, 0, 1, 0, 0];
    if (viewportTransform) {
      this.setVPT(viewportTransform, false, false);
    }
  }

  /**
   * change the viewport value in the vpt map for a specific frame
   * @param frameUid
   * @param vpt
   */
  public setVPTForFrame(frameUid: string, vpt: VPT) {
    this.viewportTransformMap[frameUid] = vpt;
  }

  // Stores the current viewport transform in a map
  // so when the user returns the board we can restore it
  public storeViewportTransform(frameUID: string | undefined) {
    if (!frameUID) {
      return;
    }

    const viewportTransform = this.viewportTransform.getValue() || [1, 0, 0, 1, 0, 0];
    this.viewportTransformMap[frameUID] = viewportTransform;
  }
}
